import { useControllableState } from '@anchorage/hooks';
import { IconButton } from '@latitude/icon-button';
import { Icon } from '@latitude/icons';
import { cn } from '@latitude/token-utils';
import { getButtonType, getButtonVariant } from 'helpers';
import type { ComponentRef, Ref } from 'react';
import type { StepperProps } from 'types';
import { valueVariants } from 'variants/value-variants';

import { buttonVariants, stepperVariants } from './variants';

/**
 * A strict and styleable stepper component.
 *
 * @component
 * @example
 * // Usage:
 * <Stepper variant="primary" size="medium" value={value} onChange={setValue}/>
 *
 * @param {Object} props - The component props.
 * @param {string} [props.className] - Additional CSS class name(s) for the stepper.
 * @param {string} [props.variant='primary'] - The stepper variant.
 * @param {string} [props.size='medium'] - The stepper size.
 * @param {number} [props.value] - The value displayed by the stepper
 * @param {number} [props.minValue=Number.MIN_SAFE_INTEGER] - The stepper's minimum allowed value.
 * @param {number} [props.maxValue=Number.MAX_SAFE_INTEGER] - The stepper's maximum allowed value.
 * @param {React.Ref<ComponentRef<'div'>>} ref - The ref passed through to the underlying stepper element.
 * @returns {React.ReactElement} The rendered Stepper component.
 */
const Stepper = ({
  variant = 'primary',
  size = 'medium',
  value: controlledValue,
  onChange: controlledOnChange,
  minValue = Number.MIN_SAFE_INTEGER,
  maxValue = Number.MAX_SAFE_INTEGER,
  'data-testid': testId = 'stepper',
  ref,
}: StepperProps & {
  ref?: Ref<ComponentRef<'div'>>;
}) => {
  const [value, setValue] = useControllableState(
    0,
    controlledValue,
    controlledOnChange,
  );

  const canDecrement = value > minValue;
  const canIncrement = value < maxValue;

  return (
    <div
      ref={ref}
      className={cn(stepperVariants({ size }))}
      data-testid={testId}
    >
      <IconButton
        variant={getButtonVariant(variant)}
        type={getButtonType(variant)}
        size={size}
        htmlType="button"
        disabled={!canDecrement}
        onClick={() => setValue(canDecrement ? value - 1 : value)}
        className={cn(buttonVariants({ size }))}
        aria-label="decrement"
        data-testid="stepper-decrement-button"
        icon={<Icon name="minus" />}
      />
      <span
        className={cn(valueVariants({ size }))}
        data-testid="stepper-value"
        aria-label="value"
      >
        {value}
      </span>
      <IconButton
        variant={getButtonVariant(variant)}
        type={getButtonType(variant)}
        size={size}
        htmlType="button"
        disabled={!canIncrement}
        onClick={() => setValue(canIncrement ? value + 1 : value)}
        className={cn(buttonVariants({ size }))}
        aria-label="increment"
        data-testid="stepper-increment-button"
        icon={<Icon name="plus" />}
      />
    </div>
  );
};

Stepper.displayName = 'Stepper';

export { Stepper };
